'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'

// walrus
import submitForm from '@lib/submit-form'
import Box, { BoxProps } from '@components/kit/box'
import Form, {
  StatusMessage,
  EmailField,
  PasswordField,
  SubmitButton,
  FormRow,
  FormFooter
} from '@components/kit/form'
import { Logotype } from '@components/kit/logo'
import Section from '@components/kit/section'
import Text from '@components/kit/text'

// lib
import { useAuthStore } from '@store/Auth'
import {
  FlipperAuthError,
  LoginCredentials,
  LoginMfaChallengeResponse
} from '@store/Auth/types'

/* -----------------------------------------------------------------------------
 * LoginForm
 * -------------------------------------------------------------------------- */

export type LoginFormProps = BoxProps & {
  onMfaRequired: (
    mfa: LoginMfaChallengeResponse,
    credentials: LoginCredentials
  ) => void
}

const LoginForm = ({ onMfaRequired, ...props }: LoginFormProps) => {
  const [flipperErrorMsg, setFlipperErrorMsg] = useState('')

  const submit = submitForm((values: LoginCredentials) => {
    useAuthStore.getState().clearAllZustandStores()
    return useAuthStore
      .getState()
      .login(values)
      .then(response => {
        if ('mfa' in response && response.mfa) {
          onMfaRequired(response, values)
        } else {
          const { errmsg, error } = response as FlipperAuthError
          setFlipperErrorMsg(errmsg || error || '')
        }
      })
      .catch(error => {
        setFlipperErrorMsg(error?.message)
      })
  })

  const onPressReturn = (e, formik) => {
    // @ts-ignore
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault()
      // unlike the button, Enter still worked while a request was out, and a
      // second login made the first code invalid
      if (!formik?.isSubmitting) formik?.handleSubmit()
    }
  }

  useEffect(() => {
    if (useAuthStore.getState().accessToken) {
      useAuthStore.getState().clearAllZustandStores()
    }
  }, [])

  return (
    <Section>
      <Logotype mb='32' />
      <Text variant='h1'>Let's get started!</Text>
      <Box {...props}>
        <Formik
          initialValues={{ email: '', password: '' }}
          onSubmit={submit}
          validateOnChange={false}
        >
          {formik => (
            <Form flex='1 0 auto'>
              <FormRow>
                <EmailField
                  name='email'
                  required
                  onKeyPress={e => onPressReturn(e, formik)}
                />
              </FormRow>
              <FormRow>
                <PasswordField
                  name='password'
                  required
                  onKeyPress={e => onPressReturn(e, formik)}
                />
              </FormRow>
              <FormFooter flex='1 0 auto' justifyContent='flex-end'>
                <StatusMessage forcedErrorMsg={flipperErrorMsg} />
                <SubmitButton>Login</SubmitButton>
              </FormFooter>
            </Form>
          )}
        </Formik>
      </Box>
    </Section>
  )
}

export default LoginForm
